// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xParserOptions } from "@rpsc/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";

const enum fields {
    mainTitle = "mainTitle",
    mainText = "mainText",
    buttonText = "buttonText",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.mainTitle]: string;
        [fields.mainText]: string;
        [fields.buttonText]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
    };
}

export interface CreateAccountButtonCardStyles {
    container?: InjectableCss;
    titleStyles?: TypographyPresentationProps;
    mainTextStyles?: TypographyPresentationProps;
    buttonStyles?: ButtonPresentationProps;
}

export const createAccountButtonCardStyles: CreateAccountButtonCardStyles = {};

const styles = createAccountButtonCardStyles;

const CreateAccountButtonCard = ({ fields }: OwnProps) => {
    const history = useHistory();
    const link = useGetLink(fields.buttonLink);
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            history.push(link.url);
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.titleStyles}>{parse(fields.mainTitle, xParserOptions)}</Typography>
            <Typography {...styles.mainTextStyles}>{parse(fields.mainText, xParserOptions)}</Typography>
            <Button {...styles.buttonStyles} variant={fields.buttonVariant} onClick={onClick}>
                {fields.buttonText}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CreateAccountButtonCard,
    definition: {
        allowedContexts: ["AccountRegistrationPage" as any],
        group: "Create Account",
        fieldDefinitions: [
            {
                name: fields.mainTitle,
                displayName: "Title",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 0,
            },
            {
                name: fields.mainText,
                displayName: "Text",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 1,
            },
            {
                name: fields.buttonText,
                displayName: "Button Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Create Account",
                sortOrder: 2,
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                sortOrder: 3,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                sortOrder: 4,
            },
        ],
    },
};

export default widgetModule;
